<template>
  <div :class="['search-tracker shadow rounded', { show }]" :style="trackerStyle">
    <div class="search-tracker-body border">
      <div class="py-2 px-3 border-bottom">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="d-flex align-items-center">
            <p class="d-inline-block search-label mr-2">
              {{ locale === 'KOR' ? '페이지 내 검색' : 'Search in Page' }}
            </p>
          </div>
          <span class="d-flex font-weight-300 px-2 mark-node rounded">
            {{ !searchedMarkedNode.length ? 0 : currentIndex + 1 }} /
            {{ !searchedMarkedNode.length ? 0 : totalSelection + 1 }}
          </span>
        </div>
        <b-input-group size="sm" class="mb-2">
          <b-form-input
            ref="searchInput"
            v-model="searchWord"
            class="search-tracker-input"
            :placeholder="locale === 'KOR' ? `검색어를 입력해주세요.` : `Please enter a keyword`"
            @keyup.enter.prevent="searchNewWord"
            @keyup.esc="closeSearchPanel"
          />
          <b-input-group-append class="position-relative">
            <b-button
              variant="gray-border"
              class="d-flex justify-content-center align-items-center search-tracker-reset"
              @click="clearSearch"
            >
              <BIconX />
            </b-button>
            <b-button
              variant="gray-border"
              class="d-flex justify-content-center align-items-center"
              @click="searchNewWord"
            >
              <BIconSearch />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
      <div class="py-2 px-3">
        <b-button-toolbar
          key-nav
          justify
          aria-label="Toolbar with button groups"
          class="page-search-toolbar"
        >
          <b-button-group class="flex-grow-1">
            <b-button
              :disabled="!searchedMarkedNode.length || currentIndex === 0"
              size="sm"
              variant="light"
              @click="goToPeer('start')"
            >
              <BIconChevronDoubleUp />
            </b-button>
            <b-button
              :disabled="!searchedMarkedNode.length || currentIndex === 0"
              size="sm"
              variant="light"
              @click="moveWord('backward')"
            >
              <BIconChevronUp />
            </b-button>
            <b-button
              :disabled="!searchedMarkedNode.length || totalSelection === currentIndex"
              size="sm"
              variant="light"
              @click="moveWord('forward')"
            >
              <BIconChevronDown />
            </b-button>
            <b-button
              :disabled="!searchedMarkedNode.length || totalSelection === currentIndex"
              size="sm"
              variant="light"
              @click="goToPeer('end')"
            >
              <BIconChevronDoubleDown />
            </b-button>
          </b-button-group>
        </b-button-toolbar>
      </div>
    </div>
    <div class="search-tracker-navigation border border-top-0 p-1" @click="show = !show">
      <p :class="['navigation-label mr-2', locale]">
        {{ locale === 'KOR' ? '페이지 내 검색' : 'Search in Page' }}
      </p>
      <svg
        class="navigation-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        xml:space="preserve"
        shape-rendering="geometricPrecision"
        text-rendering="geometricPrecision"
        image-rendering="optimizeQuality"
        fill="currentColor"
        fill-rule="evenodd"
        clip-rule="evenodd"
        viewBox="0 0 298 511.93"
      >
        <path
          fill-rule="nonzero"
          fill="currentColor"
          d="M285.77 441c16.24 16.17 16.32 42.46.15 58.7-16.16 16.24-42.45 16.32-58.69.16l-215-214.47c-16.24-16.16-16.32-42.45-.15-58.69L227.23 12.08c16.24-16.17 42.53-16.09 58.69.15 16.17 16.24 16.09 42.54-.15 58.7l-185.5 185.04L285.77 441z"
        />
      </svg>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { getParamsFromQueryString } from '@/components/util';

export default {
  name: 'SearchTracker',
  components: {},
  data() {
    return {
      show: false,
      searchWord: null,
      partialQuery: [],
      searchedMarkedNode: [],
      currentIndex: 0,
      // .navbar-sb 실제 렌더 높이(px). mounted 후 측정, ResizeObserver로 변경 추적.
      // Nav.vue CSS의 height 값이 바뀌어도 검색 패널이 자동으로 따라가기 위함.
      navbarHeight: 90
    };
  },
  computed: {
    ...mapState('report_list', ['lang', 'view_mode']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapGetters('story_board', ['selected_lv1']),
    locale() {
      return this.lang;
    },
    totalSelection() {
      return !this.searchedMarkedNode.length ? 0 : this.searchedMarkedNode.length - 1;
    },
    // StoryBoardViewer가 html 요소에 selected_report.font_size를 적용하면서
    // 검색 패널 내부 rem-기반 사이즈가 같이 바뀌는 문제 해소용 보정 비율.
    // effective 1rem = 15px이 되도록 zoom을 (15 / 보고서 font-size)로 설정.
    zoomFactor() {
      const baseFontSize = parseFloat(this.selected_report?.font_size) || 16;
      if (baseFontSize <= 0) return 1;
      return 15 / baseFontSize;
    },
    trackerStyle() {
      // zoom은 자식 사이즈뿐 아니라 top·transform offset·width까지 같이 스케일링한다.
      // baseTop/baseTranslateY/baseWidth를 zoomFactor의 역수로 보정해, 시각적
      // 위치·크기가 font-size(=zoom)에 영향받지 않도록 한다.
      //
      // baseTop = navbar 바닥(navbarHeight) + offset.
      // closed 상태에서 visual body bottom = baseTop + bodyHeightVisual + translateClosed
      //   = (navbarHeight + 50) + 132 + (-182) = navbarHeight
      // → navbar-sb height가 어떤 값이든 검색 패널 body 바닥이 항상 정렬된다.
      // (28은 selected_lv1=null의 dead-path 값 유지 - 현재 v-if로 도달 불가)
      const z = this.zoomFactor || 1;
      const offset = this.selected_lv1 === null ? 28 : 50;
      const baseTop = this.navbarHeight + offset;
      const baseTranslateY = this.show ? -48 : -182;
      const baseWidth = 250;
      return {
        top: `${baseTop / z}px`,
        transform: `translateY(${baseTranslateY / z}px)`,
        width: `${baseWidth / z}px`,
        zoom: z
      };
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Auto-focus when search panel opens
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    '$route.query.q_str'(current, before) {
      if (current) {
        this.show = true;
      }

      if (current !== before) {
        this.reset();

        this.searchWord = !current ? null : current;
        this.partialQuery = this.generatePartialQuery();
        this.integrateQueryNodes();

        this.$nextTick(() => {
          this.sortSearchedMarkNode();
          this.moveToNode();
        });
      }
    }
  },
  created() {
    this.searchWord = this.$route.query.q_str || null; // #7 fix: q_str로 올바르게 초기화
    this.partialQuery = this.generatePartialQuery();
  },
  mounted() {
    document.getSelection().removeAllRanges();
    this.integrateQueryNodes();

    this.$nextTick(() => {
      this.sortSearchedMarkNode();
      this.moveToNode();
    });

    if (this.$route.query.q_str) {
      this.show = true;
      this.reset();

      this.searchWord = this.$route.query.q_str;
      setTimeout(() => {
        this.partialQuery = this.generatePartialQuery();
        this.integrateQueryNodes();

        this.sortSearchedMarkNode();
        this.moveToNode();
      }, 1000);
    }

    this.setupNavbarHeightObserver();
  },
  beforeDestroy() {
    if (this._navbarResizeObserver) {
      this._navbarResizeObserver.disconnect();
      this._navbarResizeObserver = null;
    }
  },
  methods: {
    setupNavbarHeightObserver() {
      // .navbar-sb의 실제 렌더 높이를 측정해 navbarHeight에 반영.
      // CSS에서 height 값이 변경되거나, 추후 동적 변경이 일어나도 검색 패널이 자동 추적.
      const navbar = document.querySelector('.navbar-sb');
      if (!navbar) return;
      const h = navbar.offsetHeight;
      if (h > 0) this.navbarHeight = h;
      if (typeof ResizeObserver === 'undefined') return;
      this._navbarResizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const measured =
            (entry.borderBoxSize && entry.borderBoxSize[0] && entry.borderBoxSize[0].blockSize) ||
            (entry.contentRect && entry.contentRect.height) ||
            entry.target.offsetHeight;
          if (measured > 0) this.navbarHeight = measured;
        }
      });
      this._navbarResizeObserver.observe(navbar);
    },
    closeSearchPanel() {
      this.show = false;
    },
    reset() {
      document.getSelection().removeAllRanges();
      this.recoverMarkTag();
      this.partialQuery = [];
      this.searchedMarkedNode = [];
      this.currentIndex = 0;
    },
    clearSearch() {
      // Clear all search state and search word
      this.reset();
      this.searchWord = null;

      // Keep focus on input after clearing for better UX
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    recoverMarkTag() {
      if (this.searchedMarkedNode.length) {
        this.searchedMarkedNode.forEach(_node => {
          const parent = _node.node.parentNode;
          if (!parent) return; // #4 fix: DOM에서 이미 제거된 노드 무시
          const textNode = document.createTextNode(_node.node.textContent);
          parent.replaceChild(textNode, _node.node);
          parent.normalize(); // #4 fix: 인접 텍스트 노드 병합
        });
      }
    },
    searchNewWord() {
      // Validate search input is not empty
      if (!this.searchWord || !this.searchWord.trim()) {
        // Keep focus on input for better UX
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }

        // If search is empty, clear existing highlights
        if (!this.searchWord) {
          document.getSelection().removeAllRanges();
          this.recoverMarkTag();
          this.partialQuery = [];
          this.searchedMarkedNode = [];
          this.currentIndex = 0;
          this.$router.push({ name: this.$route.name });
        }
        return;
      }

      // Perform search
      this.reset();
      this.partialQuery = this.generatePartialQuery();
      this.integrateQueryNodes();
      this.$nextTick(() => {
        this.sortSearchedMarkNode();
        this.moveToNode();
      });
    },
    goToPeer(direction) {
      if (direction === 'start') {
        this.currentIndex = 0;
      } else {
        this.currentIndex = !this.searchedMarkedNode.length ? 0 : this.totalSelection;
      }
      this.moveToNode();
    },
    moveWord(direction) {
      if (direction === 'forward' && this.currentIndex < this.totalSelection) {
        this.currentIndex++;
      } else if (direction === 'backward' && this.currentIndex > 0) {
        this.currentIndex--;
      }

      if (this.currentIndex <= this.totalSelection && this.currentIndex > -1) {
        this.moveToNode();
      }
    },
    moveToNode() {
      if (
        this.searchedMarkedNode.length > 0 &&
        this.currentIndex > -1 &&
        this.currentIndex <= this.totalSelection
      ) {
        document.getSelection().removeAllRanges();

        const currentMovingDom = this.searchedMarkedNode[this.currentIndex].node;
        if (!currentMovingDom || !currentMovingDom.isConnected) return; // #5 fix: 분리된 노드 방어

        const range = document.createRange();
        const selection = window.getSelection();

        range.selectNode(currentMovingDom);
        selection.addRange(range);

        const _searchedDomTop = currentMovingDom.getBoundingClientRect().top + window.scrollY - 100;
        return window.scrollTo({ top: _searchedDomTop, behavior: 'smooth' });
      }
    },
    escapeXPathString(word) {
      // #2 fix: XPath 내 따옴표 처리 (작은따옴표 포함 시 concat으로 우회)
      if (!word) return "''";
      if (!word.includes("'")) return `'${word}'`;
      const parts = word.split("'").map(part => `'${part}'`);
      return `concat(${parts.join(', "\'", ')})`;
    },
    escapeRegex(word) {
      // #3 fix: 정규식 특수문자 이스케이프
      if (!word) return '';
      return word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    scrapQueryNode(word) {
      let _searchAreaXPath = null;
      const xpathWord = this.escapeXPathString(word); // #2 fix 적용
      if (this.selected_report.story_board) {
        _searchAreaXPath = `//*[@id="content"]/*[contains(.,${xpathWord})]`;
      } else {
        _searchAreaXPath = `//*[@id='standard-index-view']/*[contains(.,${xpathWord})]`;
      }

      const xpathResult = document.evaluate(
        _searchAreaXPath,
        document,
        null,
        XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
        null
      );

      // 화면에 렌더링되지 않은 요소(인쇄 전용 영역 등) 제외
      const visibleNodes = [];
      for (let i = 0; i < xpathResult.snapshotLength; i++) {
        const node = xpathResult.snapshotItem(i);
        const style = window.getComputedStyle(node);
        if (style.display !== 'none' && style.visibility !== 'hidden') {
          visibleNodes.push(node);
        }
      }
      return visibleNodes;
    },

    replaceTextOutsideCanvas(node, regex) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();
        // 검색 대상이 아닌 요소: canvas, svg, style, script 등 건너뛰기
        if (['canvas', 'svg', 'style', 'script', 'noscript'].includes(tag)) {
          return;
        }
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const matches = node.textContent.match(regex);
        if (matches) {
          const frag = document.createDocumentFragment();
          let lastIndex = 0;
          regex.lastIndex = 0;
          let match;
          while ((match = regex.exec(node.textContent)) !== null) {
            if (match[0].length === 0) break;
            const textNode = document.createTextNode(
              node.textContent.substring(lastIndex, match.index)
            );
            frag.appendChild(textNode);

            const mark = document.createElement('mark');
            mark.setAttribute('name', 'searched-word-highlight');
            const markId = `searched-word-highlight-${Date.now()}-${Math.random()}`;
            mark.setAttribute('id', markId);
            mark.textContent = match[0];

            // 필요한 경우 searchedMarkedNode에 추가
            this.searchedMarkedNode.push({
              text: match[0],
              markId: markId,
              node: mark,
              position: null
            });

            frag.appendChild(mark);
            lastIndex = regex.lastIndex;
          }
          const remainingText = node.textContent.substring(lastIndex);
          if (remainingText) {
            frag.appendChild(document.createTextNode(remainingText));
          }
          node.parentNode.replaceChild(frag, node);
        }
      } else {
        // #1 fix: 스냅샷으로 복사 후 순회 (Live NodeList 재순회 방지)
        Array.from(node.childNodes).forEach(child => {
          this.replaceTextOutsideCanvas(child, regex);
        });
      }
    },
    generateWholeQueryNodes() {
      const searchedMarkedNode = [];
      const _wholeWord = this.searchWord;
      const scrapedNodes = this.scrapQueryNode(_wholeWord);

      if (scrapedNodes.length) {
        scrapedNodes.forEach((node, index) => {
          const regex = new RegExp(this.escapeRegex(_wholeWord), 'g'); // #3 fix 적용

          this.replaceTextOutsideCanvas(node, regex, (match, offset) => {
            const markId = `searched-word-highlight-${index}-${offset}`;
            searchedMarkedNode.push({
              text: match,
              markId: markId,
              node: null,
              position: null
            });
            return `<mark name="searched-word-highlight" id="${markId}">${match}</mark>`;
          });
        });
      }
      return searchedMarkedNode;
    },
    generatePartialQuery() {
      if (!this.searchWord) {
        return [];
      } else {
        const partialQuery = this.searchWord.trim().split(' ').sort();
        const trimmedWord = this.searchWord.replace(/\s/g, '');
        if (!partialQuery.includes(trimmedWord)) {
          partialQuery.unshift(trimmedWord);
        }

        // #6 fix: splice(index)는 이후 전체 삭제 → splice(index, 1)로 1개만 제거
        if (partialQuery.includes(this.$route.query.q_str)) {
          const qStrIndex = partialQuery.findIndex(
            _partial => _partial.trim() === this.searchWord.trim()
          );
          if (qStrIndex > -1) {
            partialQuery.splice(qStrIndex, 1);
          }
        }
        return partialQuery.map(_partial => ({ keyword: _partial, searchable: true }));
      }
    },
    generatePartialQueryNodes(partialQuery) {
      const wholeQueryWord = this.searchWord;
      const searchedMarkedNode = [];

      partialQuery.forEach((_query, _pQ) => {
        const partialQueryNodes = this.scrapQueryNode(_query);

        if (partialQueryNodes.length) {
          partialQueryNodes.forEach((node, index) => {
            if (!node.textContent.includes(wholeQueryWord) && node.tagName !== 'MARK') {
              const regex = new RegExp(this.escapeRegex(_query), 'g'); // #3 fix 적용

              this.replaceTextOutsideCanvas(node, regex, (match, offset) => {
                const markId = `searched-word-highlight-${_pQ}-${index}-${offset}`;
                searchedMarkedNode.push({
                  text: match,
                  markId: markId,
                  node: null,
                  position: null
                });
                return `<mark name="searched-word-highlight" id="${markId}">${match}</mark>`;
              });
            }
          });
        }
      });
      return searchedMarkedNode;
    },
    integrateQueryNodes() {
      if (!this.searchWord || !this.searchWord.trim()) return;
      let queryNodes = this.generateWholeQueryNodes();
      let searchablePartial = this.partialQuery.filter(({ searchable }) => searchable === true);

      if (searchablePartial.length) {
        searchablePartial = searchablePartial.map(({ keyword }) => keyword);
        const _partialQueryNodes = this.generatePartialQueryNodes(searchablePartial);
        queryNodes = [...queryNodes, ..._partialQueryNodes];
      }

      if (queryNodes.length) {
        queryNodes = queryNodes.map(_markedNode => ({
          ..._markedNode,
          node: document.getElementById(_markedNode.markId)
        }));
        this.searchedMarkedNode = queryNodes;
      }
    },
    sortSearchedMarkNode() {
      if (this.searchedMarkedNode.length) {
        // #5 fix: getElementById가 null을 반환할 수 있으므로 유효한 노드만 유지
        this.searchedMarkedNode = this.searchedMarkedNode
          .map(_markedNode => {
            const el = document.getElementById(_markedNode.markId);
            if (!el) return null;
            return { ..._markedNode, position: el.getBoundingClientRect() };
          })
          .filter(Boolean);

        this.searchedMarkedNode = this.searchedMarkedNode
          .sort((a, b) => a.position.left - b.position.left)
          .sort((a, b) => a.position.top - b.position.top);
      }
    }
  }
};
</script>

<style lang="scss">
.search-tracker {
  // 인라인 zoom으로 1rem=15px 환산 보정. font-size/padding 등 rem 기반 사이즈가
  // 보고서 font-size 설정과 무관하게 일정해진다.
  position: fixed;
  right: 0;
  // width는 trackerStyle 인라인에서 zoom 보정(250 / zoomFactor) 후 적용한다.
  // 시각 너비가 항상 250px이 되도록 유지.
  display: flex;
  flex-direction: column;
  z-index: 1;
  // transform translateY는 trackerStyle 인라인에서 zoom 보정 후 적용한다.
  // CSS transition은 인라인 transform 변경에도 동일하게 동작.
  transition: transform 700ms ease-in-out 300ms;

  &-body {
    background-color: #ffffff;
  }

  &.show {
    .search-tracker-navigation {
      .navigation-label {
        transition: opacity 200ms ease-in-out, height 200ms ease-in-out 300ms,
          width 200ms ease-in-out 300ms;
        width: 0px;
        height: 0px;
        opacity: 0;
      }

      .navigation-icon {
        transform: rotate(90deg);
      }
    }
  }

  &-navigation {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909090;
    background-color: #f8f9fa;
    cursor: pointer;

    &:hover {
      color: #4e4e4e;
    }

    .navigation-label {
      transition: width 300ms ease-in-out 500ms, height 300ms ease-in-out 500ms,
        opacity 300ms ease-in-out 1200ms;
      margin-bottom: 0px;
      font-weight: 500;
      height: 28px;
      opacity: 1;
      text-align: center;

      &.en {
        width: 120px;
      }

      &:not(.en) {
        width: 150px;
      }
    }

    .navigation-icon {
      transform: rotate(-90deg);
    }
  }

  .search-label {
    margin-bottom: 0px;
  }

  .search-partial-checkbox-btn {
    .btn {
    }
  }

  .search-tracker-reset {
    &:focus {
      box-shadow: none;
      outline: none;
    }
  }

  .page-search-toolbar {
    .btn {
      font-size: 0.7rem;
    }
  }

  @media print {
    display: none !important;
  }
}

.mark-node {
  background-color: transparent;
  border: 1px solid black;
}

mark[name='searched-word-highlight'] {
  background-color: yellow;
  color: black;
}
</style>
